import React, { ReactNode, Fragment } from 'react'

const PLACEHOLDER_REGEX = /\{(\w+)\}/g

export type Renderers<T> = { [K in keyof T]?: (vars: T) => ReactNode }

/**
 * Replaces `{key}` placeholders in `text` with values from `vars`.
 * Unknown keys are left intact (e.g. `{foo}` stays `{foo}`).
 *
 * Pass `renderers` to render a placeholder as a React node (e.g. wrap in bold).
 * Returns `string` without renderers, `ReactNode` with renderers.
 *
 * @example String mode — returns a string:
 *   interpolate('Complete {n} bookings', { n: 5 })
 *   // → 'Complete 5 bookings'
 *
 * @example React mode — returns a ReactNode (array of strings + elements):
 *   interpolate('Complete {n} bookings by {date}', { n: 5, date: '30 Jun' }, {
 *     date: (vars) => <b>{vars.date}</b>,
 *   })
 *   // Renders inline as: Complete 5 bookings by <b>30 Jun</b>
 *   // (keys without a renderer fall back to their plain `vars` value)
 *
 * @example How splitting works (internal):
 *   'Complete {n} bookings by {date}'.split(/\{(\w+)\}/g)
 *   // → ['Complete ', 'n', ' bookings by ', 'date', '']
 *   //     ^literal    ^key  ^literal       ^key    ^trailing literal
 *   // Even indices = literal text (possibly empty), odd indices = captured key names.
 */
export function interpolate<T extends { [K in keyof T]: string | number }>(
  text: string,
  vars: T,
): string
export function interpolate<T extends { [K in keyof T]: string | number }>(
  text: string,
  vars: T,
  renderers: Renderers<T>,
): ReactNode
export function interpolate<T extends { [K in keyof T]: string | number }>(
  text: string,
  vars: T,
  renderers?: Renderers<T>,
): string | ReactNode {
  const varLookup = new Map(Object.entries(vars))

  // If no renderers, replace the placeholders with values
  if (!renderers) {
    return text.replace(PLACEHOLDER_REGEX, (match, key: string) => {
      return varLookup.has(key) ? String(varLookup.get(key)) : match
    })
  }

  // If renderers, replace the placeholders with React nodes
  const rendererLookup = new Map<string, (vars: T) => ReactNode>(Object.entries(renderers))

  // Even indices = literal text, odd indices = captured key names.
  return text.split(PLACEHOLDER_REGEX).map((part, i) => {
    if (i % 2 === 0) return part

    const renderer = rendererLookup.get(part)
    if (renderer) return <Fragment key={i}>{renderer(vars)}</Fragment>

    return varLookup.has(part) ? String(varLookup.get(part)) : `{${part}}`
  })
}
